import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import "./UserMassImport.css";

import {
    getAllUsersRequest,
    downloadUsersTemplateRequest,
    reviewUsersMassImportRequest,
    importUsersMassRequest,
} from "../../redux/actions/userActions";

export default function UserMassImport() {
    const dispatch = useDispatch();

    const { users, loading, massImportReview, error } = useSelector((s) => s.user);
    const currentUser      = useSelector((state) => state?.auth?.user);
    const currentCompanyId = currentUser?.companyId;

    const [mode,          setMode]          = useState("add");
    const [fileName,      setFileName]      = useState("");
    const [rows,          setRows]          = useState([]);
    const [previewQuery,  setPreviewQuery]  = useState("");
    const [existingQuery, setExistingQuery] = useState("");

    useEffect(() => { dispatch(getAllUsersRequest()); }, [dispatch]);

    const handleFile = async (file) => {
        if (!file) return;
        setFileName(file.name);
        const data  = await file.arrayBuffer();
        const wb    = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json  = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const mapped = json.map((r) => ({
            employeeCode:        String(r.employeeCode || r.EmployeeCode || r["Employee Code"] || r.Matricule || "").trim() || null,
            username:            String(r.username  || r.Username  || "").trim() || null,
            email:               String(r.email     || r.Email     || "").trim() || null,
            firstName:           String(r.firstName || r.FirstName || r.Prenom  || "").trim() || null,
            lastName:            String(r.lastName  || r.LastName  || r.Nom     || "").trim() || null,
            phone:               String(r.phone     || r.Phone     || r.GSM     || "").trim() || null,
            jobCode:             String(r.jobCode   || r.JobCode   || r["Job Code"]    || "").trim() || null,
            entityCode:          String(r.entityCode || r.EntityCode || r["Entity Code"] || "").trim() || null,
            managerEmployeeCode: String(r.managerEmployeeCode || r.ManagerEmployeeCode || r["Matricule_Manager"] || "").trim() || null,
            status:   String(r.status   || r.Status   || "").trim() || null,
            password: String(r.password || r.Password || "").trim() || null,
            typeContrat: String(r.typeContrat || r.TypeContrat || r.Type_Contrat || "").trim() || null,
            category: String(r.category || r.Category || r.Categorie || "").trim() || null,
        })).filter((r) => r.employeeCode);
        setRows(mapped);
    };

    const clearFile = () => { setFileName(""); setRows([]); setPreviewQuery(""); };

    const filteredExisting = useMemo(() => {
        const q = existingQuery.trim().toLowerCase();
        if (!q) return users || [];
        return (users || []).filter((u) => {
            const emp  = (u.employeeCode || "").toLowerCase();
            const mail = (u.email || "").toLowerCase();
            const name = (u.fullName || `${u.firstName || ""} ${u.lastName || ""}`).toLowerCase();
            return emp.includes(q) || mail.includes(q) || name.includes(q);
        });
    }, [users, existingQuery]);

    const filteredPreview = useMemo(() => {
        const q = previewQuery.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            (r.employeeCode || "").toLowerCase().includes(q) ||
            (r.email        || "").toLowerCase().includes(q) ||
            (`${r.firstName || ""} ${r.lastName || ""}`).toLowerCase().includes(q)
        );
    }, [rows, previewQuery]);

    const onDownloadTemplate = () => dispatch(downloadUsersTemplateRequest());

    const onReview = () => {
        if (!currentCompanyId) return alert("CompanyId introuvable.");
        if (!rows.length)      return alert("Aucune ligne (importe un Excel).");
        dispatch(reviewUsersMassImportRequest(currentCompanyId, mode, rows));
    };

    const onImport = () => {
        if (!currentCompanyId) return alert("CompanyId introuvable.");
        if (!rows.length)      return alert("Aucune ligne à importer.");
        if (!massImportReview) return alert("Lance d'abord la vérification.");
        if (hasErrors)         return alert("Corrige d'abord les erreurs.");
        dispatch(importUsersMassRequest(currentCompanyId, mode, rows));
    };

    const hasErrors = (massImportReview?.errors || []).length > 0;

    return (
        <div className="umi-page">

            {/* ── Header ─────────────────────────── */}
            <div className="umi-header">
                <div>
                    <h2>Liste des utilisateurs</h2>
                    <p className="umi-subtitle">Ajoutez ou mettez à jour des utilisateurs en masse via un fichier Excel</p>
                </div>
                <div className="umi-header-actions">
                    <div className="umi-segmented">
                        <button type="button" className={`umi-seg-btn ${mode === "add"  ? "active" : ""}`} onClick={() => setMode("add")}>Ajouter</button>
                        <button type="button" className={`umi-seg-btn ${mode === "edit" ? "active" : ""}`} onClick={() => setMode("edit")}>Mettre à jour</button>
                    </div>
                    <button type="button" onClick={onDownloadTemplate} className="umi-btn umi-btn-outline">
                        Télécharger la template
                    </button>
                </div>
            </div>

            {/* ── Section unique : Excel + Vérifier + Importer ── */}
            <div className="umi-section">
                <div className="umi-section-header umi-section-header--simple">
                    <div className="umi-section-title-wrap">
                        <div className="umi-section-dot coral" />
                        <h3>Fichier Excel &amp; Validation</h3>
                    </div>
                </div>

                {/* Upload + boutons alignés */}
                <div className="umi-upload-actions-row">
                    <label className="umi-file-label">
                        <input type="file" accept=".xlsx,.xls"
                               onChange={(e) => handleFile(e.target.files?.[0])}
                               className="umi-file-input"
                        />
                        <span className="umi-file-icon">📂</span>
                        <span>{fileName || "Choisir un fichier .xlsx / .xls"}</span>
                    </label>

                    {fileName && (
                        <button type="button" className="umi-btn umi-btn-outline" onClick={clearFile}>
                            Changer
                        </button>
                    )}

                    <div className="umi-row-sep" />

                    <button type="button" onClick={onReview} className="umi-btn umi-btn-outline" disabled={loading}>
                        Vérifier
                    </button>
                    <button type="button" onClick={onImport} className="umi-btn umi-btn-primary"
                            disabled={!massImportReview || hasErrors || loading}>
                        {loading ? "Traitement..." : "Importer"}
                    </button>
                </div>

                {/* Preview */}
                {!!rows.length && (
                    <>
                        <div className="umi-toolbar">
                            <div className="umi-meta">
                                <span className="umi-count-pill">{rows.length}</span>
                                ligne{rows.length !== 1 ? "s" : ""} importée{rows.length !== 1 ? "s" : ""}
                            </div>
                            <input className="umi-search" placeholder="Filtrer le preview..."
                                   value={previewQuery} onChange={(e) => setPreviewQuery(e.target.value)} />
                        </div>
                        <div className="umi-table-wrapper">
                            <table className="umi-table">
                                <thead>
                                <tr>
                                    <th>Matricule</th><th>Nom</th><th>Email</th>
                                    <th>JobCode</th><th>EntityCode</th><th>ManagerCode</th>
                                    <th>Type</th><th>Catégorie</th><th>Statut</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredPreview.slice(0, 20).map((r, idx) => (
                                    <tr key={`${r.employeeCode}-${idx}`}>
                                        <td><span className="umi-code">{r.employeeCode}</span></td>
                                        <td className="umi-name">{`${r.firstName || ""} ${r.lastName || ""}`.trim() || "—"}</td>
                                        <td className="umi-muted-cell">{r.email || "—"}</td>
                                        <td className="umi-muted-cell">{r.jobCode || "—"}</td>
                                        <td className="umi-muted-cell">{r.entityCode || "—"}</td>
                                        <td className="umi-muted-cell">{r.managerEmployeeCode || "—"}</td>
                                        <td className="umi-muted-cell"><span className="umi-badge secondary">{r.typeContrat || "—"}</span></td>
                                        <td className="umi-muted-cell">{r.category || "—"}</td>
                                        <td>{r.status ? <span className="umi-badge">{r.status}</span> : "—"}</td>
                                    </tr>
                                ))}
                                {!filteredPreview.length && (
                                    <tr><td colSpan={7} className="umi-empty">Aucune ligne (filtre trop strict ?)</td></tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                        <p className="umi-hint">Prévisualisation limitée aux 20 premières lignes.</p>
                    </>
                )}

                {/* Résultats validation */}
                {error && <div className="umi-alert umi-alert-danger" style={{ marginTop: 14 }}>{error}</div>}
                {loading && <div className="umi-loading"><div className="umi-spinner" /><span>Chargement...</span></div>}

                {massImportReview && (
                    <div className="umi-review">
                        <div className="umi-review-stats">
                            <div className="umi-stat"><span className="umi-stat-value">{massImportReview.totalRows}</span><span className="umi-stat-label">Total</span></div>
                            <div className="umi-stat green"><span className="umi-stat-value">{massImportReview.validRows}</span><span className="umi-stat-label">Valides</span></div>
                            <div className="umi-stat coral"><span className="umi-stat-value">{(massImportReview.errors || []).length}</span><span className="umi-stat-label">Erreurs</span></div>
                            <div className="umi-stat orange"><span className="umi-stat-value">{(massImportReview.warnings || []).length}</span><span className="umi-stat-label">Warnings</span></div>
                        </div>
                        {!hasErrors ? (
                            <div className="umi-alert umi-alert-success">Fichier valide — vous pouvez lancer l'import.</div>
                        ) : (
                            <div className="umi-alert umi-alert-danger">
                                <div className="umi-alert-title">Erreurs à corriger</div>
                                <ul className="umi-alert-list">{(massImportReview.errors || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
                            </div>
                        )}
                        {!!(massImportReview.warnings || []).length && (
                            <div className="umi-alert umi-alert-warn">
                                <div className="umi-alert-title">Avertissements</div>
                                <ul className="umi-alert-list">{(massImportReview.warnings || []).map((w, i) => <li key={i}>{w}</li>)}</ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Section 2 : Utilisateurs existants ────── */}
            <div className="umi-section">
                <div className="umi-section-header">
                    <div className="umi-section-title-wrap">
                        <div className="umi-section-dot blue" />
                        <h3>Utilisateurs existants</h3>
                        <span className="umi-count">{(users || []).length}</span>
                    </div>
                    <input className="umi-search" placeholder="Rechercher (matricule, email, nom...)"
                           value={existingQuery} onChange={(e) => setExistingQuery(e.target.value)} />
                </div>
                <div className="umi-table-wrapper">
                    <table className="umi-table">
                        <thead>
                        <tr>
                            <th>Matricule</th><th>Nom complet</th><th>Email</th>
                            <th>Poste</th><th>Entité</th><th>Manager</th>
                            <th>Contrat</th><th>Catégorie</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(filteredExisting || []).slice(0, 15).map((u) => (
                            <tr key={u.id}>
                                <td><span className="umi-code">{u.employeeCode || "—"}</span></td>
                                <td className="umi-name">{u.fullName || `${u.firstName || ""} ${u.lastName || ""}`}</td>
                                <td className="umi-muted-cell">{u.email || "—"}</td>
                                <td className="umi-muted-cell">{u.jobName || u.job?.code || "—"}</td>
                                <td className="umi-muted-cell">{u.entityName || u.entity?.code || "—"}</td>
                                <td className="umi-muted-cell">{u.managerFullName || "—"}</td>
                                <td className="umi-muted-cell"><span className="umi-badge secondary">{u.typeContrat || "—"}</span></td>
                                <td className="umi-muted-cell">{u.category || "—"}</td>
                            </tr>
                        ))}
                        {!filteredExisting?.length && (
                            <tr><td colSpan={6} className="umi-empty">Aucun utilisateur trouvé</td></tr>
                        )}
                        </tbody>
                    </table>
                </div>
                <p className="umi-hint">Affichage des 15 premiers résultats.</p>
            </div>

        </div>
    );
}